<script setup lang="ts">
    import { columns } from '@/tables/columns/log';
    import { DatePicker } from '@/components/ui/form';
    import { Head } from '@inertiajs/vue3';
    import { onUnmounted, ref } from 'vue';
    import { useLogStore } from '@/stores/log';
    import { useQuery, useQueryClient } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableExpandFilters from '@/components/datatable/DataTableExpandFilters.vue';
    import DataTableSearchButton from '@/components/datatable/DataTableSearchButton.vue';
    import LogFilters from '@/components/log/LogFilters.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import SystemCardMenu from '@/components/system/SystemCardMenu.vue';
    import type { PaginationState } from '@tanstack/vue-table';
    import type { BreadcrumbItem } from '@/types';

    const logStore = useLogStore();
    const totalRecords = ref(0);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.settings',
        },
        {
            title: 'menu.system',
            href: route('v2.vat-deadlines.index'),
        },
    ];

    const queryClient = useQueryClient();

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.LOGS],
        queryFn: getLogs,
    });

    async function getLogs() {
        const { data } = await axios.get(route('v2.log.table'), {
            params: {
                fromDate: logStore.startDate,
                toDate: logStore.endDate,
                selectedRows: logStore.selectedRows,
                length: logStore.pagination.pageSize,
                start: logStore.pagination.pageIndex * logStore.pagination.pageSize,
            },
        });
        totalRecords.value = data.recordsTotal;
        const rows = data?.data ?? [];
        return rows
            .slice()
            .sort(
                (a: any, b: any) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
    }

    const handlePaginationChange = (newPagination: PaginationState) => {
        logStore.updatePagination(newPagination);
        refetch();
    };

    const handleRefetch = () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: [QueryKey.ACTIVITIES] });
    };

    onUnmounted(() => {
        logStore.$reset();
    });
</script>

<template>
    <Head :title="$t('activities.page_header')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard class="min-h-auto">
            <DataTable
                :table-key="QueryKey.LOGS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :enable-search="false"
                :enable-filters="false"
                :manual-sorting="true"
                :default-column-visibility="{
                    id: false,
                }"
                :manual-pagination="true"
                :total-records="totalRecords"
                :default-pagination="logStore.pagination"
                @pagination-change="handlePaginationChange"
                toolbar-class="sm:flex-wrap lg:flex-nowrap lg:items-center"
            >
                <template #title>
                    <SystemCardMenu />
                </template>
                <template #actions>
                    <div class="flex flex-wrap items-center gap-4 sm:flex-nowrap">
                        <span class="text-[10px] uppercase">
                            {{ $t('activities.date_selection') }}
                        </span>
                        <DatePicker v-model="logStore.startDate" />
                        <DatePicker v-model="logStore.endDate" />
                        <DataTableSearchButton
                            @refetch="handleRefetch"
                            :is-fetching="isFetching"
                        />
                        <DataTableExpandFilters />
                    </div>
                </template>
                <template #table-top>
                    <LogFilters />
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
